import { useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { adoptDiskSessionIfFresh, adoptDiskSessionAfterRefreshRejection } from '@/lib/native/sessionBridge';

interface StaffMember {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  team: string;
  role_level: 'user' | 'admin' | 'superadmin' | 'adviser';
}

interface UserProfile {
  user_id: string;
  staff_member_id: string | null;
  role_level: 'user' | 'admin' | 'superadmin' | 'adviser';
  email?: string;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [staffMember, setStaffMember] = useState<StaffMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  useEffect(() => {
    console.log('🔐 useAuth: Setting up auth state listener');
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('🔐 useAuth: Auth state changed', { 
          event, 
          hasSession: !!session, 
          userEmail: session?.user?.email,
          expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'N/A'
        });
        
        // Handle sign out
        if (event === 'SIGNED_OUT') {
          console.log('🚪 useAuth: User signed out');
          setSession(null);
          setUser(null);
          setStaffMember(null);
          setLoading(false);
          return;
        }
        
        // Update state synchronously
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer async operations with setTimeout
        if (session?.user) {
          setTimeout(() => {
            handleSessionUpdate(event, session);
          }, 0);
        } else {
          setStaffMember(null);
          setLoading(false);
        }
      }
    );

    // Check for existing session
    const initializeAuth = async () => {
      console.log('🔍 useAuth: Checking for existing session...');
      
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('❌ useAuth: Error getting session:', error);
          setLoading(false);
          return;
        }
        
        console.log('🔍 useAuth: Initial session check', { 
          hasSession: !!session, 
          userEmail: session?.user?.email,
          expiresAt: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'N/A'
        });
        
        if (session?.user) {
          // Check if session is expired
          const expiresAt = session.expires_at;
          const now = Math.floor(Date.now() / 1000);
          
          if (expiresAt && expiresAt < now) {
            // Rotation-race guard (WS-LINK, plan §5 G2): before redeeming the refresh token ourselves,
            // check whether a native process (e.g. Android Auto) already rotated it while the WebView
            // was backgrounded. If disk is already meaningfully ahead, adopt it instead of racing.
            const adopted = await adoptDiskSessionIfFresh(expiresAt);
            if (adopted) {
              const { data: { session: adoptedSession } } = await supabase.auth.getSession();
              if (adoptedSession) {
                console.log('✅ useAuth: Adopted a fresher on-disk session instead of refreshing');
                setSession(adoptedSession);
                setUser(adoptedSession.user);
                await fetchStaffMemberWithTimeout(adoptedSession.user.email!);
                return;
              }
            }

            console.warn('⚠️ useAuth: Initial session expired, refreshing...');
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

            if (refreshError) {
              // Rotation-race guard (G2): a rejection can mean the OTHER process already redeemed this
              // refresh token and won the race, not that the user is signed out. Re-read disk fresh
              // before hard-signing-out — only escalate if disk is also expired/invalid.
              const adoptedAfterRejection = await adoptDiskSessionAfterRefreshRejection();
              if (adoptedAfterRejection) {
                const { data: { session: adoptedSession } } = await supabase.auth.getSession();
                if (adoptedSession) {
                  console.log('✅ useAuth: Adopted disk session after a losing initial-refresh race');
                  setSession(adoptedSession);
                  setUser(adoptedSession.user);
                  await fetchStaffMemberWithTimeout(adoptedSession.user.email!);
                  return;
                }
              }

              console.error('❌ useAuth: Initial session refresh failed - refresh token expired. Clearing all auth data.');
              await supabase.auth.signOut();
              setSession(null);
              setUser(null);
              setStaffMember(null);
              setLoading(false);
              return;
            }

            if (refreshData.session) {
              console.log('✅ useAuth: Initial session refreshed');
              setSession(refreshData.session);
              setUser(refreshData.session.user);
              await fetchStaffMemberWithTimeout(refreshData.session.user.email!);
              return;
            }
          }
          
          setSession(session);
          setUser(session.user);
          
          // Don't update login stats on initial session check - only on actual sign-in events
          await fetchStaffMemberWithTimeout(session.user.email!);
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ useAuth: Exception during auth initialization:', error);
        setLoading(false);
      }
    };

    initializeAuth();

    return () => subscription.unsubscribe();
  }, []);

  const handleSessionUpdate = async (event: string, session: Session) => {
    console.log('🔄 useAuth: Handling session update asynchronously');
    
    // Only update login stats for actual sign-in events (OAuth redirects)
    // Check if this is a new login by verifying last_login_at is more than 5 minutes ago
    if (event === 'SIGNED_IN') {
      try {
        // Check last login time before updating
        const { data: profile } = await supabase
          .from('profiles')
          .select('last_login_at')
          .eq('user_id', session.user.id)
          .single();
        
        const lastLogin = profile?.last_login_at ? new Date(profile.last_login_at) : null;
        const now = new Date();
        const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
        
        // Only increment if last login was more than 5 minutes ago (indicates new session)
        if (!lastLogin || lastLogin < fiveMinutesAgo) {
          await supabase.rpc('update_login_stats', { user_uuid: session.user.id });
          console.log('✅ useAuth: Login stats updated for new session');
        } else {
          console.log('🔄 useAuth: Skipping login stats update - recent login detected');
        }
      } catch (error) {
        console.error('❌ useAuth: Failed to update login stats:', error);
      }
    } else if (event === 'TOKEN_REFRESHED') {
      console.log('🔄 useAuth: Token refreshed successfully - no login count increment');
    }
    
    // Check if session is expired or about to expire
    const expiresAt = session.expires_at;
    const now = Math.floor(Date.now() / 1000);
    
    if (expiresAt && expiresAt < now) {
      // Rotation-race guard (WS-LINK, plan §5 G2): skip our own refresh if disk already shows a
      // fresher session (native already rotated it) — adopt instead of racing to redeem the same
      // single-use refresh token.
      const adopted = await adoptDiskSessionIfFresh(expiresAt);
      if (adopted) {
        const { data: { session: adoptedSession } } = await supabase.auth.getSession();
        if (adoptedSession) {
          console.log('✅ useAuth: Adopted a fresher on-disk session instead of refreshing');
          setSession(adoptedSession);
          setUser(adoptedSession.user);
        }
      } else {
        console.warn('⚠️ useAuth: Session expired, attempting refresh...');
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();

        if (refreshError) {
          // Rotation-race guard (G2 — "the single most important correctness fix"): a rejection can
          // mean a concurrent native refresh already won the race, not that the session is dead.
          // Re-read disk fresh before hard-signing-out.
          const adoptedAfterRejection = await adoptDiskSessionAfterRefreshRejection();
          if (adoptedAfterRejection) {
            const { data: { session: adoptedSession } } = await supabase.auth.getSession();
            if (adoptedSession) {
              console.log('✅ useAuth: Adopted disk session after a losing refresh race');
              setSession(adoptedSession);
              setUser(adoptedSession.user);
            }
          } else {
            console.error('❌ useAuth: Session refresh failed - refresh token expired. Clearing all auth data.');
            await supabase.auth.signOut();
            setSession(null);
            setUser(null);
            setStaffMember(null);
            setLoading(false);
            return;
          }
        } else if (refreshData.session) {
          console.log('✅ useAuth: Session refreshed successfully');
          setSession(refreshData.session);
          setUser(refreshData.session.user);
        }
      }
    }

    // Fetch staff member with timeout protection
    await fetchStaffMemberWithTimeout(session.user.email!);
  };

  const fetchStaffMemberWithTimeout = async (email: string) => {
    console.log('👤 useAuth: Fetching staff member for email:', email);
    
    // Create a timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Staff member fetch timeout')), 10000); // 10 second timeout
    });
    
    // Create the fetch promise
    const fetchPromise = fetchStaffMember(email);
    
    try {
      await Promise.race([fetchPromise, timeoutPromise]);
    } catch (error) {
      console.error('❌ useAuth: Staff member fetch failed or timed out:', error);
      setStaffMember(null);
      setLoading(false);
    }
  };

  const fetchStaffMember = async (email: string) => {
    try {
      console.log('📊 useAuth: Querying profiles table for current user...');
      
      // First, get the user's profile (which has role_level and staff_member_id)
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('⚠️ useAuth: No authenticated user found');
        setStaffMember(null);
        setLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('user_id, staff_member_id, role_level, force_password_change, team')
        .eq('user_id', user.id)
        .single();
      
      if (profileError) {
        console.error('❌ useAuth: Profile query error:', profileError);
        setStaffMember(null);
        setLoading(false);
        return;
      }

      if (!profile || !profile.staff_member_id) {
        console.warn('⚠️ useAuth: User has no staff member association');
        setStaffMember(null);
        setLoading(false);
        return;
      }

      console.log('✅ useAuth: Profile found', { 
        staff_member_id: profile.staff_member_id,
        role_level: profile.role_level,
        force_password_change: profile.force_password_change
      });

      // Set forced password change flag
      setForcePasswordChange(!!profile.force_password_change);

      // Create a minimal staff member object from profile and user metadata
      // This avoids querying staff_members table which has problematic RLS
      const minimalStaffMember: StaffMember = {
        id: profile.staff_member_id,
        email: user.email || email,
        first_name: user.user_metadata?.first_name || email.split('@')[0],
        last_name: user.user_metadata?.last_name || '',
        role: profile.role_level,
        team: profile.team || '',
        role_level: profile.role_level
      };

      console.log('✅ useAuth: Created minimal staff member from profile', { 
        name: `${minimalStaffMember.first_name} ${minimalStaffMember.last_name}`, 
        role_level: minimalStaffMember.role_level 
      });
      
      setStaffMember(minimalStaffMember);
    } catch (error) {
      console.error('❌ useAuth: Exception fetching staff member:', error);
      setStaffMember(null);
    } finally {
      console.log('✅ useAuth: Setting loading to false');
      setLoading(false);
    }
  };

  const signOut = async () => {
    console.log('🚪 useAuth: Signing out...');
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setStaffMember(null);
  };

  // Role hierarchy: superadmin ⊇ admin ⊇ adviser ⊇ user. A principal satisfies a gate when their
  // rank is at least the LOWEST rank the gate asks for, so a higher role always inherits every
  // permission of the roles beneath it. Unknown / non-hierarchical role strings still match exactly.
  const ROLE_RANK: Record<string, number> = {
    user: 1,
    adviser: 2,
    admin: 3,
    superadmin: 4,
  };

  const hasRole = (requiredRoles: string[]): boolean => {
    if (!staffMember) {
      console.log('⚠️ useAuth: hasRole check failed - no staff member');
      return false;
    }

    const current = (staffMember.role_level || '').toLowerCase();
    const wanted = requiredRoles.map((r) => (r || '').toLowerCase());

    // Exact match on anything (covers non-hierarchical values).
    if (wanted.includes(current)) return true;

    const currentRank = ROLE_RANK[current];
    if (!currentRank) return false;

    const ranks = wanted.map((r) => ROLE_RANK[r]).filter((r): r is number => !!r);
    if (ranks.length === 0) return false;

    return currentRank >= Math.min(...ranks);
  };


  const canAccessPortfolioManagement = (): boolean => {
    // 'senior analyst' was a dead grant (no job title exists client-side, so it never matched).
    // Gate on role_level only; if senior analysts should have access, add a real signal (a
    // job-title field, or a dedicated role_level / team) — none exists today.
    return hasRole(['admin', 'superadmin']);
  };

  const canAccessAdmin = (): boolean => {
    return hasRole(['admin', 'superadmin']);
  };

  return {
    user,
    session,
    staffMember,
    loading,
    forcePasswordChange,
    setForcePasswordChange,
    signOut,
    hasRole,
    canAccessPortfolioManagement,
    canAccessAdmin,
    isAuthenticated: !!user && !!session,
    // Phase 3 (F3): a parallel, purely-additive derived flag for the external-adviser path.
    // True only for an authenticated principal with NO staff identity (staffMember is null) —
    // i.e. retail or external advisers, never staff. The staff path above is untouched.
    isNonStaffAuthenticated: !!user && !!session && !staffMember
  };
};